import React, { useState } from 'react';
import { BookmarkIcon, DownloadIcon, ChevronRightIcon, ArrowDownIcon, ArrowUpIcon, GridIcon, ListIcon } from 'lucide-react';
// Mock data for search results
const tenders = [{
  id: 1,
  title: "Construction d'un centre hospitalier à Dakar",
  country: 'Sénégal',
  sector: 'Santé',
  date: '2023-10-15',
  deadline: '2023-11-20',
  budget: '2.5M €',
  status: 'Ouvert',
  new: true,
  description: "Appel d'offres pour la construction d'un nouveau centre hospitalier moderne de 200 lits avec services spécialisés à Dakar."
}, {
  id: 2,
  title: "Fourniture d'équipements informatiques pour écoles",
  country: 'Maroc',
  sector: 'Éducation',
  date: '2023-10-12',
  deadline: '2023-11-15',
  budget: '850K €',
  status: 'Ouvert',
  new: true,
  description: "Fourniture et installation d'équipements informatiques pour 50 écoles dans les régions rurales du Maroc."
}, {
  id: 3,
  title: "Développement d'un système d'irrigation agricole",
  country: "Côte d'Ivoire",
  sector: 'Agriculture',
  date: '2023-10-10',
  deadline: '2023-11-10',
  budget: '1.2M €',
  status: 'Ouvert',
  new: false,
  description: "Conception et mise en œuvre d'un système d'irrigation moderne pour 1000 hectares de terres agricoles dans la région de Yamoussoukro."
}, {
  id: 4,
  title: 'Installation de panneaux solaires pour zones rurales',
  country: 'Kenya',
  sector: 'Énergie',
  date: '2023-10-08',
  deadline: '2023-11-05',
  budget: '3.1M €',
  status: 'Ouvert',
  new: false,
  description: "Installation de systèmes d'énergie solaire dans 30 villages ruraux pour fournir de l'électricité à environ 15 000 habitants."
}, {
  id: 5,
  title: 'Construction de routes et infrastructures urbaines',
  country: 'Tunisie',
  sector: 'Infrastructure',
  date: '2023-10-05',
  deadline: '2023-10-30',
  budget: '4.7M €',
  status: 'Urgent',
  new: false,
  description: 'Réhabilitation de 25 km de routes urbaines et construction de nouvelles infrastructures dans la ville de Sousse.'
}, {
  id: 6,
  title: 'Fourniture de médicaments et équipements médicaux',
  country: 'Algérie',
  sector: 'Santé',
  date: '2023-10-03',
  deadline: '2023-10-28',
  budget: '1.8M €',
  status: 'Ouvert',
  new: false,
  description: 'Approvisionnement en médicaments essentiels et équipements médicaux pour les hôpitaux publics dans plusieurs wilayas.'
}];
interface SearchResultsProps {
  query: string;
  filters: {
    countries: string[];
    sectors: string[];
    budgetMin: string;
    budgetMax: string;
    dateRange: any;
  };
}
export function SearchResults({
  query,
  filters
}: SearchResultsProps) {
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [sortField, setSortField] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  // Filter tenders based on search query and filters
  // This is just a simple mock implementation
  const filteredTenders = tenders.filter(tender => {
    // Filter by query
    if (query && !tender.title.toLowerCase().includes(query.toLowerCase())) {
      return false;
    }
    // Filter by countries
    if (filters.countries.length > 0 && !filters.countries.includes(tender.country)) {
      return false;
    }
    // Filter by sectors
    if (filters.sectors.length > 0 && !filters.sectors.includes(tender.sector)) {
      return false;
    }
    // More filters would be applied here in a real implementation
    return true;
  });
  // Sort tenders
  const sortedTenders = [...filteredTenders].sort((a, b) => {
    if (sortField === 'date') {
      return sortDirection === 'asc' ? new Date(a.date).getTime() - new Date(b.date).getTime() : new Date(b.date).getTime() - new Date(a.date).getTime();
    } else if (sortField === 'deadline') {
      return sortDirection === 'asc' ? new Date(a.deadline).getTime() - new Date(b.deadline).getTime() : new Date(b.deadline).getTime() - new Date(a.deadline).getTime();
    } else if (sortField === 'budget') {
      const aBudget = parseFloat(a.budget.replace(/[^0-9.]/g, ''));
      const bBudget = parseFloat(b.budget.replace(/[^0-9.]/g, ''));
      return sortDirection === 'asc' ? aBudget - bBudget : bBudget - aBudget;
    }
    return 0;
  });
  return <div className="bg-white rounded-xl shadow-md overflow-hidden" data-id="element-742">
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between" data-id="element-743">
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-744">
          {filteredTenders.length} résultats trouvés
        </h2>
        <div className="mt-2 sm:mt-0 flex items-center space-x-4" data-id="element-745">
          {/* View mode toggle */}
          <div className="flex items-center space-x-1 bg-gray-100 rounded-md p-1" data-id="element-746">
            <button onClick={() => setViewMode('list')} className={`p-1 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-747">
              <ListIcon size={18} data-id="element-748" />
            </button>
            <button onClick={() => setViewMode('grid')} className={`p-1 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-749">
              <GridIcon size={18} data-id="element-750" />
            </button>
          </div>
          {/* Sort dropdown */}
          <div className="relative inline-block text-left" data-id="element-751">
            <select className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" value={`${sortField}-${sortDirection}`} onChange={e => {
            const [field, direction] = e.target.value.split('-');
            setSortField(field);
            setSortDirection(direction as 'asc' | 'desc');
          }} data-id="element-752">
              <option value="date-desc" data-id="element-753">Date: Plus récent</option>
              <option value="date-asc" data-id="element-754">Date: Plus ancien</option>
              <option value="deadline-asc" data-id="element-755">Échéance: Plus proche</option>
              <option value="deadline-desc" data-id="element-756">Échéance: Plus lointaine</option>
              <option value="budget-desc" data-id="element-757">Budget: Plus élevé</option>
              <option value="budget-asc" data-id="element-758">Budget: Plus bas</option>
            </select>
          </div>
        </div>
      </div>
      {viewMode === 'list' ?
    // List view
    <div className="overflow-x-auto" data-id="element-759">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-760">
            <thead className="bg-gray-50" data-id="element-761">
              <tr data-id="element-762">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-763">
                  Titre
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-764">
                  Pays
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-765">
                  Secteur
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('deadline')} data-id="element-766">
                  <div className="flex items-center" data-id="element-767">
                    Date limite
                    {sortField === 'deadline' && (sortDirection === 'asc' ? <ArrowUpIcon size={14} className="ml-1" data-id="element-768" /> : <ArrowDownIcon size={14} className="ml-1" data-id="element-769" />)}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('budget')} data-id="element-770">
                  <div className="flex items-center" data-id="element-771">
                    Budget
                    {sortField === 'budget' && (sortDirection === 'asc' ? <ArrowUpIcon size={14} className="ml-1" data-id="element-772" /> : <ArrowDownIcon size={14} className="ml-1" data-id="element-773" />)}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-774">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-775">
              {sortedTenders.map(tender => <tr key={tender.id} className="hover:bg-gray-50" data-id="element-776">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-777">
                    <div className="flex items-center" data-id="element-778">
                      <div data-id="element-779">
                        <div className="text-sm font-medium text-gray-900 flex items-center" data-id="element-780">
                          {tender.title}
                          {tender.new && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-781">
                              Nouveau
                            </span>}
                          {tender.status === 'Urgent' && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-782">
                              Urgent
                            </span>}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-783">
                          Publié le{' '}
                          {new Date(tender.date).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-784">
                    <div className="text-sm text-gray-900" data-id="element-785">
                      {tender.country}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-786">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-787">
                      {tender.sector}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-788">
                    {new Date(tender.deadline).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-789">
                    {tender.budget}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-790">
                    <div className="flex justify-end space-x-2" data-id="element-791">
                      <button className="text-gray-400 hover:text-blue-600" data-id="element-792">
                        <BookmarkIcon size={18} data-id="element-793" />
                      </button>
                      <button className="text-gray-400 hover:text-blue-600" data-id="element-794">
                        <DownloadIcon size={18} data-id="element-795" />
                      </button>
                      <button className="text-blue-600 hover:text-blue-800" data-id="element-796">
                        <ChevronRightIcon size={18} data-id="element-797" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div> :
    // Grid view
    <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-798">
          {sortedTenders.map(tender => <div key={tender.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-id="element-799">
              <div className="p-4" data-id="element-800">
                <div className="flex justify-between" data-id="element-801">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-802">
                    {tender.sector}
                  </span>
                  <div className="flex space-x-1" data-id="element-803">
                    {tender.new && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-804">
                        Nouveau
                      </span>}
                    {tender.status === 'Urgent' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-805">
                        Urgent
                      </span>}
                  </div>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900 line-clamp-2" data-id="element-806">
                  {tender.title}
                </h3>
                <p className="mt-1 text-xs text-gray-500 line-clamp-2" data-id="element-807">
                  {tender.description}
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-808">
                  <div data-id="element-809">
                    <span className="text-xs text-gray-500" data-id="element-810">Pays</span>
                    <p className="text-sm font-medium text-gray-900" data-id="element-811">
                      {tender.country}
                    </p>
                  </div>
                  <div data-id="element-812">
                    <span className="text-xs text-gray-500" data-id="element-813">Budget</span>
                    <p className="text-sm font-medium text-gray-900" data-id="element-814">
                      {tender.budget}
                    </p>
                  </div>
                  <div data-id="element-815">
                    <span className="text-xs text-gray-500" data-id="element-816">Publication</span>
                    <p className="text-sm font-medium text-gray-900" data-id="element-817">
                      {new Date(tender.date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div data-id="element-818">
                    <span className="text-xs text-gray-500" data-id="element-819">Échéance</span>
                    <p className="text-sm font-medium text-gray-900" data-id="element-820">
                      {new Date(tender.deadline).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 flex justify-between items-center" data-id="element-821">
                <button className="text-xs text-blue-600 font-medium hover:text-blue-800" data-id="element-822">
                  Voir les détails
                </button>
                <div className="flex space-x-2" data-id="element-823">
                  <button className="text-gray-400 hover:text-blue-600" data-id="element-824">
                    <BookmarkIcon size={16} data-id="element-825" />
                  </button>
                  <button className="text-gray-400 hover:text-blue-600" data-id="element-826">
                    <DownloadIcon size={16} data-id="element-827" />
                  </button>
                </div>
              </div>
            </div>)}
        </div>}
      {/* Pagination */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-828">
        <div className="text-sm text-gray-500" data-id="element-829">
          Affichage de {sortedTenders.length} appels d'offres sur{' '}
          {tenders.length}
        </div>
        <div className="flex space-x-1" data-id="element-830">
          <button className="px-3 py-1 rounded-md text-sm text-gray-600 hover:bg-gray-100" data-id="element-831">
            Précédent
          </button>
          <button className="px-3 py-1 rounded-md text-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-832">
            1
          </button>
          <button className="px-3 py-1 rounded-md text-sm text-gray-600 hover:bg-gray-100" data-id="element-833">
            2
          </button>
          <button className="px-3 py-1 rounded-md text-sm text-gray-600 hover:bg-gray-100" data-id="element-834">
            3
          </button>
          <button className="px-3 py-1 rounded-md text-sm text-gray-600 hover:bg-gray-100" data-id="element-835">
            Suivant
          </button>
        </div>
      </div>
    </div>;
}